import * as THREE from 'three';
import type { Settings } from '../core/Settings';
import type { GameClock } from '../core/Time';
import type { BiomeDef } from '../data/biomes';

/**
 * Render katmanı: renderer kurulumu, gökyüzü, güneş/ay ışığı, sis ve yıldızlar.
 * Kalite profili değiştiğinde pixelRatio ve gölge ayarları anında uygulanır.
 */

const DAY_SKY = new THREE.Color(0x8ec5e8);
const DUSK_SKY = new THREE.Color(0xe8895a);
const NIGHT_SKY = new THREE.Color(0x0a1020);
const DAY_LIGHT = new THREE.Color(0xfff4e0);
const NIGHT_LIGHT = new THREE.Color(0x93b4ff);

export class RenderSystem {
  readonly renderer: THREE.WebGLRenderer;
  readonly camera: THREE.PerspectiveCamera;
  readonly sun: THREE.DirectionalLight;
  readonly hemi: THREE.HemisphereLight;
  private stars: THREE.Points;
  private sunDisc: THREE.Mesh;
  private skyColor = new THREE.Color();
  private fogColor = new THREE.Color();
  private targetFog = 0.0016;

  constructor(canvas: HTMLCanvasElement, private settings: Settings) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false, // mobilde pahalı; pixelRatio ile dengeliyoruz
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.camera = new THREE.PerspectiveCamera(settings.graphics.fov, window.innerWidth / window.innerHeight, 0.1, 900);

    this.hemi = new THREE.HemisphereLight(0xbfd8ff, 0x4a5a3a, 0.55);
    this.sun = new THREE.DirectionalLight(0xfff4e0, 1.2);
    this.sun.castShadow = true;
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 220;
    const d = 60;
    this.sun.shadow.camera.left = -d;
    this.sun.shadow.camera.right = d;
    this.sun.shadow.camera.top = d;
    this.sun.shadow.camera.bottom = -d;
    this.sun.shadow.bias = -0.0008;

    // Yıldızlar: tek Points nesnesi, gece görünür
    const starGeo = new THREE.BufferGeometry();
    const count = 420;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 420;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 0.9 + 0.05);
      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.cos(phi) * 0.7 + 40;
      pos[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
    }
    starGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.stars = new THREE.Points(starGeo, new THREE.PointsMaterial({ color: 0xffffff, size: 2.2, sizeAttenuation: false, transparent: true }));
    this.stars.visible = false;

    this.sunDisc = new THREE.Mesh(new THREE.SphereGeometry(14, 12, 8), new THREE.MeshBasicMaterial({ color: 0xfff2c4 }));

    this.applyQuality();
    settings.events.on('graphics:changed', () => this.applyQuality());
    window.addEventListener('resize', this.onResize);
  }

  /** Sahneye ışıkları ekler (dünya değiştiğinde yeniden çağrılır). */
  attach(scene: THREE.Scene): void {
    // Kamera sahne grafiğinde değilse altındaki birinci-şahıs viewmodeli
    // renderer tarafından hiç dolaşılmaz. Kamerayı eklemek world renderını
    // etkilemez, fakat held-item gibi kamera çocuklarını gerçek piksel olarak
    // çizilebilir kılar.
    scene.add(this.camera);
    scene.add(this.hemi);
    scene.add(this.sun);
    scene.add(this.sun.target);
    scene.add(this.stars);
    scene.add(this.sunDisc);
    scene.fog = new THREE.FogExp2(0x8ec5e8, this.targetFog);
  }

  applyQuality(): void {
    const p = this.settings.profile;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, p.pixelRatio));
    this.renderer.shadowMap.enabled = p.shadows;
    this.sun.castShadow = p.shadows;
    if (p.shadows) {
      this.sun.shadow.mapSize.setScalar(p.shadowMapSize);
      // Gölge haritası boyutu değiştiyse yeniden oluşsun
      this.sun.shadow.map?.dispose();
      this.sun.shadow.map = null as unknown as THREE.WebGLRenderTarget;
    }
    this.camera.fov = this.settings.graphics.fov;
    this.camera.updateProjectionMatrix();
  }

  private onResize = (): void => {
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  };

  /** Gökyüzü/ışık güncellemesi. */
  update(dt: number, clock: GameClock, scene: THREE.Scene, playerPos: THREE.Vector3, biome: BiomeDef, underwater: boolean): void {
    const elev = clock.sunElevation; // -1..1
    const dayT = Math.max(0, elev);
    const duskT = Math.max(0, 1 - Math.abs(elev) * 4);

    // Gökyüzü rengi: gece -> şafak/akşam -> gündüz
    this.skyColor.copy(NIGHT_SKY).lerp(DAY_SKY, dayT);
    this.skyColor.lerp(DUSK_SKY, duskT * 0.55);
    // Biyom sisini harmanla
    this.fogColor.setHex(biome.fog).lerp(this.skyColor, 0.45);
    if (underwater) {
      this.skyColor.setHex(0x14384f);
      this.fogColor.setHex(0x14384f);
    }
    scene.background = this.skyColor;
    const fog = scene.fog as THREE.FogExp2 | null;
    if (fog) {
      const target = underwater ? 0.055 : biome.fogDensity * this.settings.profile.fogDensityScale;
      fog.density += (target - fog.density) * Math.min(1, dt * 2);
      fog.color.copy(this.fogColor);
    }

    // Işık
    const nightVision = 0;
    this.sun.intensity = 0.15 + dayT * 1.25 + nightVision;
    this.sun.color.copy(NIGHT_LIGHT).lerp(DAY_LIGHT, dayT);
    this.hemi.intensity = 0.18 + dayT * 0.5;
    this.hemi.color.copy(this.skyColor);

    // Güneş konumu oyuncuyu takip eder (gölge kamerası dar tutulur)
    const angle = (clock.cycle - 0.25) * Math.PI * 2;
    const sx = Math.cos(angle) * 120;
    const sy = Math.sin(angle) * 120;
    this.sun.position.set(playerPos.x + sx, Math.max(12, playerPos.y + sy), playerPos.z + 60);
    this.sun.target.position.copy(playerPos);
    this.sunDisc.position.set(playerPos.x + sx * 2.6, playerPos.y + sy * 2.6, playerPos.z + 150);
    this.sunDisc.visible = sy > -20;
    (this.sunDisc.material as THREE.MeshBasicMaterial).color.setHex(duskT > 0.4 ? 0xffb45a : 0xfff2c4);

    // Yıldızlar
    this.stars.visible = elev < 0.08 && !underwater;
    (this.stars.material as THREE.PointsMaterial).opacity = Math.min(1, Math.max(0, -elev * 3 + 0.25));
    this.stars.position.copy(playerPos);
  }

  render(scene: THREE.Scene): void {
    this.renderer.render(scene, this.camera);
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    this.stars.geometry.dispose();
    (this.stars.material as THREE.Material).dispose();
    this.sunDisc.geometry.dispose();
    (this.sunDisc.material as THREE.Material).dispose();
    this.renderer.dispose();
  }
}
